import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  ChevronLeft,
  Eye,
  ShoppingBag,
  DollarSign,
  Star,
  Heart,
  Users,
  TrendingUp,
  TrendingDown,
  Sparkles,
  MessageCircle,
  Trophy,
  Activity,
} from "lucide-react";

const WINDOWS = [
  { key: 7, label: "7 days" },
  { key: 30, label: "30 days" },
  { key: 90, label: "90 days" },
];

function DeltaPill({ pct }) {
  if (pct === null || pct === undefined) return null;
  const positive = pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${
        positive
          ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/30"
          : "bg-red-500/15 text-red-300 border-red-400/30"
      }`}
      data-testid="insights-delta-pill"
    >
      <Icon className="w-3 h-3" />
      {positive ? "+" : ""}
      {pct}%
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, hint, accent = "pink", delta, testid }) {
  const ring =
    accent === "pink"
      ? "from-pink-500/15 border-pink-400/25 text-pink-200"
      : accent === "cyan"
      ? "from-cyan-500/15 border-cyan-400/25 text-cyan-200"
      : accent === "amber"
      ? "from-amber-500/15 border-amber-400/25 text-amber-200"
      : accent === "emerald"
      ? "from-emerald-500/15 border-emerald-400/25 text-emerald-200"
      : "from-white/10 border-white/15 text-white";
  return (
    <div
      className={`rounded-2xl p-4 bg-gradient-to-br ${ring} to-transparent border`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-[10px] uppercase tracking-widest font-black opacity-80 truncate">{label}</p>
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
      </div>
      <p className="text-2xl font-black tracking-tight text-white">{value}</p>
      <div className="mt-1 flex items-center gap-2">
        {delta !== undefined && delta !== null && <DeltaPill pct={delta} />}
        {hint && <p className="text-[11px] text-zinc-400 truncate">{hint}</p>}
      </div>
    </div>
  );
}

function MiniSparkline({ points, valueKey = "revenue" }) {
  const values = points.map((p) => Number(p[valueKey] || 0));
  const max = Math.max(...values, 1);
  const W = 600;
  const H = 80;
  const step = points.length > 1 ? W / (points.length - 1) : 0;
  const coords = values.map((v, i) => {
    const x = i * step;
    const y = H - (v / max) * (H - 8) - 4;
    return [x, y];
  });
  const linePath = coords.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
  const areaPath = `${linePath} L${W},${H} L0,${H} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20" preserveAspectRatio="none">
      <defs>
        <linearGradient id="sparkGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgb(236,72,153)" stopOpacity="0.45" />
          <stop offset="100%" stopColor="rgb(236,72,153)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#sparkGrad)" />
      <path d={linePath} fill="none" stroke="rgb(236,72,153)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrendCard({ trend }) {
  const totalRevenue = useMemo(() => trend.reduce((s, t) => s + (t.revenue || 0), 0), [trend]);
  const totalOrders = useMemo(() => trend.reduce((s, t) => s + (t.orders || 0), 0), [trend]);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="insights-trend-card">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-black inline-flex items-center gap-1.5">
          <Activity className="w-3 h-3 text-pink-300" /> Revenue trend
        </p>
        <p className="text-xs text-zinc-400 font-bold">
          ${totalRevenue.toFixed(2)} · {totalOrders} orders
        </p>
      </div>
      {trend.length === 0 ? (
        <p className="text-center text-zinc-500 text-sm py-6">No data yet</p>
      ) : (
        <MiniSparkline points={trend} valueKey="revenue" />
      )}
      <div className="mt-2 flex justify-between text-[10px] text-zinc-500 font-bold">
        <span>{trend[0]?.date || "—"}</span>
        <span>{trend[trend.length - 1]?.date || "—"}</span>
      </div>
    </div>
  );
}

function TopProductsCard({ products }) {
  if (!products?.length) return null;
  const max = Math.max(...products.map((p) => p.qty_sold || p.views || 0), 1);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="insights-top-products">
      <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-black mb-3 inline-flex items-center gap-1.5">
        <Trophy className="w-3 h-3 text-amber-300" /> Top performers
      </p>
      <div className="space-y-2">
        {products.map((p, idx) => {
          const val = p.qty_sold || p.views || 0;
          const pct = (val / max) * 100;
          const usingViews = !p.qty_sold && p.views !== undefined;
          return (
            <div key={p.product_id || idx} className="space-y-1" data-testid={`insights-top-product-${idx}`}>
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-sm font-bold truncate flex-1">
                  <span className="text-pink-300 mr-1">#{idx + 1}</span>
                  {p.name}
                </p>
                <p className="text-xs font-black text-pink-200 shrink-0">
                  {usingViews
                    ? `${val} views`
                    : `${p.qty_sold} sold · $${(p.revenue || 0).toFixed(2)}`}
                </p>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-pink-400 to-cyan-400"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function VendorInsights() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/sell/insights", { params: { days } });
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load insights");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.is_vendor) {
      toast.error("Vendor only");
      navigate("/sell", { replace: true });
      return;
    }
    load();
  }, [authLoading, user, navigate, load]);

  if (authLoading || !user) return null;

  const h = data?.headline || {};

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5 pb-24" data-testid="vendor-insights-page">
      <button
        onClick={() => navigate("/sell")}
        className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white"
        data-testid="insights-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back to shop
      </button>

      <div className="rounded-3xl p-5 bg-gradient-to-br from-cyan-500/15 via-pink-500/10 to-transparent border border-cyan-400/25">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-black tracking-tighter inline-flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-cyan-300" /> Shop insights
            </h1>
            <p className="text-xs text-zinc-300">
              Live snapshot of how <span className="text-pink-300 font-bold">@{user.vendor_profile?.business_name || user.name}</span> is performing.
            </p>
          </div>
          <div className="inline-flex rounded-full border border-white/10 bg-white/5 overflow-hidden" data-testid="insights-window-picker">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                onClick={() => setDays(w.key)}
                className={`h-9 px-3 text-xs font-bold uppercase tracking-widest ${
                  days === w.key ? "bg-pink-500/25 text-pink-100" : "text-zinc-300 hover:bg-white/5"
                }`}
                data-testid={`insights-window-${w.key}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && !data ? (
        <p className="text-center text-zinc-500 py-10">Crunching the numbers…</p>
      ) : !data ? (
        <p className="text-center text-zinc-500 py-10">No data yet.</p>
      ) : (
        <>
          {/* Headline KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <KpiCard
              icon={DollarSign}
              label={`Revenue (${days}d)`}
              value={`$${(h.revenue_window || 0).toFixed(2)}`}
              delta={h.revenue_delta_pct}
              hint={`$${(h.revenue_alltime || 0).toFixed(2)} all-time`}
              accent="pink"
              testid="kpi-revenue"
            />
            <KpiCard
              icon={ShoppingBag}
              label={`Orders (${days}d)`}
              value={h.orders_window || 0}
              delta={h.orders_delta_pct}
              hint={`${h.completed_orders || 0} completed all-time`}
              accent="cyan"
              testid="kpi-orders"
            />
            <KpiCard
              icon={Eye}
              label="Product views"
              value={h.total_views || 0}
              hint={`${h.total_likes || 0} ❤ likes`}
              accent="emerald"
              testid="kpi-views"
            />
            <KpiCard
              icon={Star}
              label="Rating"
              value={h.avg_rating ? `${h.avg_rating}★` : "—"}
              hint={`${h.review_count || 0} reviews`}
              accent="amber"
              testid="kpi-rating"
            />
            <KpiCard
              icon={Users}
              label="Followers"
              value={h.followers || 0}
              hint={`+${h.followers_window || 0} in ${days}d`}
              accent="pink"
              testid="kpi-followers"
            />
            <KpiCard
              icon={Heart}
              label="Tips"
              value={`$${(h.tips_total || 0).toFixed(2)}`}
              hint={`${h.tips_count || 0} tippers`}
              accent="pink"
              testid="kpi-tips"
            />
            <KpiCard
              icon={MessageCircle}
              label="Reply rate"
              value={`${h.response_rate || 0}%`}
              hint="DM responsiveness"
              accent="cyan"
              testid="kpi-response"
            />
            <KpiCard
              icon={ShoppingBag}
              label="Pending"
              value={h.pending_orders || 0}
              hint="Waiting on you"
              accent="amber"
              testid="kpi-pending"
            />
            <KpiCard
              icon={ShoppingBag}
              label="Cancelled"
              value={h.cancelled_orders || 0}
              hint="Lifetime"
              accent="default"
              testid="kpi-cancelled"
            />
          </div>

          {/* Trend chart */}
          <TrendCard trend={data.trend || []} />

          {/* Top products */}
          <TopProductsCard products={data.top_products || []} />

          {/* Tips card */}
          <div className="rounded-2xl border border-pink-400/20 bg-gradient-to-br from-pink-500/10 to-transparent p-4" data-testid="insights-coach-card">
            <p className="text-[10px] uppercase tracking-widest text-pink-300 font-black mb-1.5">Coach</p>
            <p className="text-sm text-zinc-200 leading-snug">
              {h.orders_delta_pct >= 0
                ? `Orders are ${h.orders_delta_pct === 0 ? "steady" : `up ${h.orders_delta_pct}%`} vs the previous window. Keep posting stories & live streams — they correlate with views.`
                : `Orders dipped ${Math.abs(h.orders_delta_pct)}% — try a 24h $1 boost or a flash promo on your top product to relight the algorithm.`}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
